import { z } from 'zod'

import { getHostRuntime } from '../../../core/engine/host-runtime'
import { FIELD_TYPE_OPERATORS } from '@/Atomic/Organisms/FilterBuilder/config'
import { defineTool } from '@/Atomic/Widgets/AiChatBot/core/engine/runtime/tool-system'
import {
  mergeSameFieldConditions,
  mergeSameFieldGroups,
  normalizeEnumCondition,
} from '@/Atomic/Widgets/AiChatBot/core/tools/filter-normalizer'
import { DataClassName } from '@/Data/const'

const structureSchema = z.object({
  or: z.array(
    z.object({
      and: z.array(
        z.object({
          field: z.string(),
          op: z.string(),
          value: z.unknown(),
        }),
      ),
    }),
  ),
})

export const saveFilterTool = defineTool({
  name: 'save_filter',
  description:
    'Save the current filter as a named saved filter (visible in the FilterBuilder sidebar dropdown). ' +
    'Call only when the user explicitly asks to save a filter by name (e.g. "save this filter as X"). ' +
    'Shows a confirmation card before writing. Never call this without an explicit save intent from the user. ' +
    'IMPORTANT: Always pass the exact `structure` returned by show_filter_preview (the `structure` field in the apply result). Never reconstruct or rephrase the structure from conversation context — reconstructed structures contain unresolved labels that will be rejected.',
  concurrencySafe: false,
  cacheable: false,
  inputSchema: () =>
    z.object({
      title: z.string().describe('Name for the saved filter'),
      entityType: z.string().describe('Entity type this filter applies to'),
      structure: z
        .preprocess(
          (v) => (typeof v === 'string' ? JSON.parse(v) : v),
          structureSchema,
        )
        .describe('The SavedFilterStructure from build_filter'),
      description: z
        .string()
        .describe('Human-readable summary of what the filter selects'),
    }),

  async run(args, ctx, toolCallId) {
    if (!toolCallId) {
      return { error: 'save_filter requires a toolCallId' }
    }

    const { data, navigation: nav } = getHostRuntime()
    const id = toolCallId
    const confirmed = await new Promise<boolean>((resolve, reject) => {
      ctx.registerToolCallResolver?.(
        id,
        {
          type: 'confirm',
          toolCallId: id,
          spec: {
            title: 'Save filter',
            rows: [
              { label: 'Name', value: args.title, source: 'user' as const },
              {
                label: 'Applies to',
                value: args.entityType,
                source: 'resolved' as const,
              },
              {
                label: 'Filter',
                value: args.description,
                source: 'resolved' as const,
              },
            ],
          },
        },
        (result: unknown) => resolve(result === true),
        reject,
      )
    })

    if (!confirmed) {
      return { cancelled: true }
    }

    // Prefer the filter actually applied on the list page (a selected saved filter
    // or the inline `?query=`) over the LLM-passed structure. The model reconstructs
    // filters from conversation and drifts (unresolved labels, duplicate same-field
    // groups). The applied filter is the source of truth the user sees — reading it
    // makes bot-save identical to the manual "save current filter". Fall back to the
    // LLM structure only when nothing is applied (e.g. "save a filter for X" without
    // applying first).
    const structure = (await data.getAppliedFilterStructure()) ?? args.structure

    // Resolve display labels → PISA codes using schema, same flow as build_filter.
    // Returns an error if any enum value cannot be resolved — prevents saving invalid PISA codes.
    try {
      const { schemaAttributes } = await data.getSchema(
        data.getSchemaName(args.entityType),
      )
      if (schemaAttributes) {
        const enumErrors: string[] = []

        for (const group of structure.or) {
          for (const cond of group.and) {
            const attr = schemaAttributes[cond.field]
            if (!attr) continue
            const fieldType = attr.type
            const allowedOps = FIELD_TYPE_OPERATORS[fieldType]
            if (!allowedOps) continue
            if (!allowedOps.includes(cond.op as never)) {
              cond.op = allowedOps[0] ?? cond.op
            }
            if (
              (fieldType === 'enum' || fieldType === 'reference') &&
              attr.enumValues?.length
            ) {
              // Canonical enum normalization shared with the manual builder:
              // label→code, flip eq→in, expand same-title codes.
              const normalized = normalizeEnumCondition(
                cond.op,
                cond.value,
                attr.enumValues,
              )

              // Validate that all resolved values are real PISA codes
              const validCodes = new Set(attr.enumValues.map((e) => e.value))
              const resolvedValues = Array.isArray(normalized.value)
                ? (normalized.value as unknown[])
                : normalized.value !== null
                  ? [normalized.value]
                  : []
              const unresolved = resolvedValues
                .map(String)
                .filter((v) => !validCodes.has(v))
              if (unresolved.length > 0) {
                const validLabels = [
                  ...new Set(attr.enumValues.map((e) => e.title)),
                ]
                enumErrors.push(
                  `Field "${cond.field}" ("${attr.title}"): ${unresolved.map((v) => `"${v}"`).join(', ')} did not match any enum value. Valid display labels: ${validLabels.map((l) => `"${l}"`).join(', ')}`,
                )
              } else {
                cond.op = normalized.op
                cond.value = normalized.value
              }
            }
          }
        }

        if (enumErrors.length > 0) {
          return {
            error: `Cannot save filter — contains unresolvable enum values. Fix the values and call build_filter again before saving.\n\n${enumErrors.join('\n')}`,
          }
        }

        // Merge same-field in/nin conditions within each AND group (e.g. two
        // "group in [x]" conditions → one "group in [x, y]"). An AND of both is
        // a contradiction for single-valued fields and PISA rejects it (422).
        for (const group of structure.or) {
          group.and = mergeSameFieldConditions(group.and)
        }
        // Collapse single-condition OR groups on the same field+op into one
        // unioned group — "(group in [CST]) OR (group in [LEA])" → "group in
        // [CST, LEA]", the canonical shape the manual builder produces.
        structure.or = mergeSameFieldGroups(structure.or)
      }
    } catch {
      // Schema load failed — save with the structure as-is
    }

    const item = (await data.create(DataClassName.FILTER, {
      entity: args.entityType,
      title: args.title,
      favorite: false,
      kind: 'neocrm',
      query: { filters: structure },
    })) as { id: () => string }

    // Mirror the UI save path (EntitySearchHeader.handleSaveAsFilter): select the
    // newly-saved filter by id and drop the unsaved `query` param. Composing write
    // preserves `order` and other params on the current list URL.
    nav.writeUrlParams({ filter: item.id(), query: null })

    return {
      saved: true,
      id: item.id(),
      title: args.title,
      entityType: args.entityType,
    }
  },
})
